/**
 * SmartEvaluate Engine Blueprint
 * -----------------------------------------------------------------------------
 * Structured, consulting-grade reference of the scoring, recommendation, ROI,
 * and implementation logic that powers the SmartEvaluate platform. Content is
 * system-focused (rules and mappings), not instructional — this is the
 * product blueprint, not a user guide.
 */

const A_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"];

export default function LogicReferencePage() {
  return (
    <div data-testid="logic-reference-page" className="max-w-5xl">
      {/* Page header */}
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold mb-1">
          Engine Blueprint
        </p>
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
          SmartEvaluate Engine Blueprint
        </h1>
        <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">
          System-level blueprint describing how the SmartEvaluate Engine transforms client inputs into
          recommendations, implementation plans, ROI, confidence scores and executive reports. This
          reusable engine will power future SmartEvaluate modules including Transportation,
          Manufacturing and other domains.
        </p>
      </div>

      {/* Pipeline strip */}
      <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3 mb-6" data-testid="logic-pipeline">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">Pipeline</p>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#111827]">
          <Pill>1. Inputs</Pill><Arrow />
          <Pill>2. Scoring</Pill><Arrow />
          <Pill>3. Recommendation</Pill><Arrow />
          <Pill>4. ROI</Pill><Arrow />
          <Pill>5. Implementation</Pill><Arrow />
          <Pill>6. Report</Pill>
        </div>
      </div>

      {/* Engine architecture */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 mb-6" data-testid="logic-architecture">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-4">Engine Architecture</p>
        <div className="flex flex-col items-center gap-1.5">
          {["Question Bank", "Answer Engine", "Scoring Engine", "Decision Engine", "Outputs"].map((n, i, arr) => (
            <div key={n} className="flex flex-col items-center gap-1.5">
              <div className="inline-flex items-center justify-center min-w-[190px] px-4 py-2 rounded-sm bg-[#F0F9FF] border border-[#025979]/25 text-[12px] font-semibold text-[#0F172A]">
                {n}
              </div>
              {i < arr.length - 1 && <span className="text-[#9CA3AF] text-sm leading-none">↓</span>}
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-dashed border-[#E5E7EB]">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2 text-center">Outputs branch into</p>
          <div className="flex flex-wrap justify-center gap-2 text-[11px]">
            {["Recommendation", "Confidence", "Risks", "Opportunities", "ROI", "Implementation Plan", "Executive Report"].map((o) => (
              <Pill key={o}>{o}</Pill>
            ))}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* A. Evaluation Framework                                         */}
      {/* ------------------------------------------------------------- */}
      <Section letter="A" title="Evaluation Framework">
        <P>
          Three-step, multi-site capable evaluation driven by an admin-editable question bank.
          Each step narrows scope from enterprise context to per-warehouse process depth.
        </P>
        <Table
          head={["Step", "Module", "Dimension captured", "Output"]}
          rows={[
            ["1", "Company Profile", "Client, scenario (greenfield / brownfield)", "Evaluation scope & scenario type"],
            ["2", "Warehouse Landscape", "Industry, network, strategic objective, warehouse types", "Representative sites selection"],
            ["3", "Process Evaluation", "Per-site process answers across 8 sections", "Capability scores per site"],
          ]}
        />
        <P>
          Step 3 sections drive section-local scoring: <em>Org Structure · Inbound · Outbound ·
          Inventory · Automation · Integration · Reporting · Pain Points &amp; Future Plans</em>.
          Production and Migration Readiness are optional add-ons rendered conditionally.
        </P>
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* B. Capability Scoring Model                                     */}
      {/* ------------------------------------------------------------- */}
      <Section letter="B" title="Scoring Engine">
        <P>
          Every answer value resolves to a complexity intensity on a fixed 0 / 1 / 2 scale. Intensities
          are aggregated to the section level (average) and to the site level (weighted average).
        </P>
        <Table
          head={["Intensity", "Signal tokens in the option value", "Score"]}
          rows={[
            ["Low", "low · basic · simple · single · manual · none · minimal", "0"],
            ["Medium", "medium · moderate · structured · batch · wave · scheduled", "1"],
            ["High", "high · advanced · complex · real-time · multi-site · RFID · predictive", "2"],
            ["Unknown", "Any value not matching the above (dropdown label variations)", "1 (neutral)"],
          ]}
        />
        <P>
          Multi-select questions take the <strong>highest</strong> contributing intensity, so a single
          advanced selection is not diluted by low-intensity peers.
        </P>
        <Table
          head={["Aggregate", "Definition", "Consumer"]}
          rows={[
            ["Section average", "Mean intensity across answered questions in that section", "PRIVA Insight Engine (F), decision triggers (C)"],
            ["Site score", "Section-average, equal-weighted across visible sections", "Recommendation (C), ROI baseline (E)"],
            ["Tab completion %", "Answered required fields ÷ total required fields per tab", "Dashboard status, finalisation gate"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* C. Recommendation Logic                                         */}
      {/* ------------------------------------------------------------- */}
      <Section letter="C" title="Decision Engine">
        <P>
          Decision rules (stored in <Code>capability_matrix</Code>, admin-editable) map business
          signals to specific SAP capabilities. Capabilities roll up to an overall strategy type.
        </P>
        <Table
          head={["Trigger source", "Decision rule", "Capability produced"]}
          rows={[
            ["Inbound.hu_usage = high", "+1 to handling-unit capability", "HU Management"],
            ["Outbound.order_complexity ≥ high AND outbound_volume ≥ high", "+1 to wave & batch capability", "Wave / Batch Orchestration"],
            ["Inventory.inventory_tracking = high OR batch_tracking ∈ {batch, serial}", "+1 to traceability capability", "Batch / Serial Tracking"],
            ["Automation.automation_level = high OR RF usage = high", "+1 to execution capability", "RF-led Execution / Advanced Automation"],
            ["Integration.systems_count ≥ 3 OR real_time_required = high", "+1 to integration capability", "Synchronised Integration"],
            ["Reporting.reporting_level = advanced OR predictive", "+1 to analytics capability", "Embedded / Predictive Analytics"],
          ]}
        />
        <Table
          head={["Overall site strategy", "Qualification rule"]}
          rows={[
            ["Foundational execution", "Average capability score < 0.6 across all capabilities"],
            ["Structured execution", "Score 0.6 – 1.2 with at least two capabilities triggered"],
            ["Advanced execution", "Score 1.2 – 1.7 with automation OR integration triggered"],
            ["Transformational", "Score ≥ 1.7 with automation AND integration triggered"],
          ]}
        />
        <P>
          Cross-site strategy (overall evaluation) = highest tier reached by any representative site.
          Stored on the assessment document as <Code>overall_strategy_type</Code>.
        </P>
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* D. Implementation Estimation Logic                              */}
      {/* ------------------------------------------------------------- */}
      <Section letter="D" title="Implementation Estimation Logic">
        <P>
          Estimates are sourced from <Code>implementation_templates</Code> (admin-editable) keyed on
          <em> scenario × strategy × site-size bucket</em>. Each template provides per-phase durations,
          day-rates, and FTE mix that drive the Excel workbook export.
        </P>
        <Table
          head={["Site size bucket", "Deriving field", "Typical duration range"]}
          rows={[
            ["Small", "warehouse_size = small OR throughput = low", "8 – 14 weeks"],
            ["Medium", "warehouse_size = medium OR throughput = medium", "12 – 20 weeks"],
            ["Large", "warehouse_size = large OR throughput = high", "18 – 32 weeks"],
            ["Multi-site", "Representative sites ≥ 2", "Add 15 – 35 % overlay per additional site"],
          ]}
        />
        <Table
          head={["Phase", "Weight", "Derivation"]}
          rows={[
            ["Prepare", "10 – 15 %", "Fixed minimum per scenario"],
            ["Explore", "20 – 25 %", "Scaled by site count and scenario (brownfield = +20 %)"],
            ["Realize", "40 – 50 %", "Scaled by capability count + automation trigger"],
            ["Deploy", "15 – 20 %", "Scaled by integration capability"],
            ["Run", "5 – 10 %", "Fixed hyper-care window"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* E. ROI Calculation Logic                                        */}
      {/* ------------------------------------------------------------- */}
      <Section letter="E" title="Business Case Engine">
        <P>
          ROI uses client-editable assumptions stored in <Code>roi_assumptions</Code>. Benefits stem
          from labour productivity, inventory accuracy, and throughput uplift; cost stems from the
          implementation estimate in (D). All figures convert to the client currency via the
          configured rate.
        </P>
        <Formula>
          <strong>Annual Benefit</strong> = Σ(Benefit Lever<sub>i</sub> × Lever Scale<sub>i</sub>) × Strategy Uplift %<br/>
          <strong>Total Cost</strong> = Σ(Phase Duration × Phase Day-Rate × FTE Count)<br/>
          <strong>Payback (months)</strong> = (Total Cost / Annual Benefit) × 12<br/>
          <strong>3-Yr NPV</strong> = Σ<sub>t=1..3</sub>(Annual Benefit × (1 − Ramp-up Factor<sub>t</sub>)) − Total Cost
        </Formula>
        <Table
          head={["Benefit lever", "Default factor", "Derivation"]}
          rows={[
            ["Labour productivity", "10 – 25 %", "Outbound volume × avg pick cost × efficiency uplift"],
            ["Inventory accuracy", "1 – 3 % of inventory value", "SKU count × avg value × shrink reduction"],
            ["Throughput uplift", "5 – 15 %", "Only applied when automation capability ≥ 1"],
            ["Service level", "Qualitative", "Not monetised by default — surfaced in report narrative"],
          ]}
        />
        <Table
          head={["Scenario band", "Rule", "Sensitivity applied"]}
          rows={[
            ["Conservative", "Automation ≤ 0 OR data confidence = low", "Benefits × 0.7"],
            ["Baseline", "Mid-range triggers", "Benefits × 1.0"],
            ["Accelerated", "Automation ≥ 1 AND integration ≥ 1", "Benefits × 1.25"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* F. Insight Engine (Ask PRIVA)                                  */}
      {/* ------------------------------------------------------------- */}
      <Section letter="F" title="PRIVA Insight Engine">
        <P>
          Produces one sentence of interpretive insight for the section the user is currently reading
          in Step 3. Fully rule-based; no LLM, no random text, no stacked messages.
        </P>
        <Table
          head={["Template bucket", "Selection rule (section-average intensity)"]}
          rows={[
            ["T1 · Simple / Basic", "avg &lt; 0.45"],
            ["T2 · Structured / Moderate", "0.45 ≤ avg &lt; 0.95"],
            ["T3 · Distributed / HU / Batch / RF", "0.95 ≤ avg &lt; 1.35"],
            ["T4 · Variable / Growing / High", "1.35 ≤ avg &lt; 1.70"],
            ["T5 · Complex / Advanced / Transformational", "avg ≥ 1.70"],
            ["Placeholder", "Fewer than 2 answered questions in the section"],
          ]}
        />
        <Table
          head={["Context signal", "Behaviour"]}
          rows={[
            ["Active section changes on scroll", "Panel replaces content — never stacks"],
            ["Intensity moves across threshold", "Template index advances; copy swaps with a clean transition"],
            ["Tab switch (site change)", "Insight re-computes against the new site's answers"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* G. Help Knowledge                                               */}
      {/* ------------------------------------------------------------- */}
      <Section letter="G" title="Help Knowledge">
        <P>
          Curated SAP / WMS glossary stored in <Code>help_knowledge</Code>. Surfaced via the
          Ask PRIVA side drawer and editable in Admin · Help Knowledge.
        </P>
        <Table
          head={["Field", "Purpose"]}
          rows={[
            ["term", "Canonical label (unique, case-insensitive)"],
            ["keywords", "Lowercase search aliases for keyword and substring matching"],
            ["definition", "One- or two-sentence product-neutral description"],
            ["why_it_matters", "One-sentence business relevance"],
            ["example", "One concrete example, phrased conversationally"],
            ["category", "Org · Inbound · Outbound · Inventory · Automation · Integration · Reporting · PainPoints"],
            ["active", "Inactive entries are hidden from Ask PRIVA but visible in Admin"],
          ]}
        />
        <Table
          head={["Match precedence", "Rule"]}
          rows={[
            ["1", "Exact match on term (case-insensitive)"],
            ["2", "Exact match on any keyword"],
            ["3", "Substring match on term OR keyword"],
            ["Fallback", "Enterprise not-found message — no AI generation"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* H. Final Report Alignment                                       */}
      {/* ------------------------------------------------------------- */}
      <Section letter="H" title="Final Report Alignment">
        <P>
          All outputs flow into a single, branded deliverable. Report sections derive from the
          previous stages — no content is hand-authored at render time.
        </P>
        <Table
          head={["Report section", "Source"]}
          rows={[
            ["Executive Summary", "Scenario + overall strategy (C) + ROI headline (E)"],
            ["Capability Matrix", "Per-section scores (B) + triggered capabilities (C)"],
            ["Recommendations", "admin-editable recommendation_texts keyed by capability"],
            ["ROI Business Case", "Calculations (E), currency converted"],
            ["Implementation Plan", "Per-phase durations + rates (D), exportable to Excel"],
            ["Appendix", "Raw client inputs, version number, branding assets"],
          ]}
        />
        <Table
          head={["Channel", "Mechanism"]}
          rows={[
            ["Consultant PDF", "Server-side Playwright render of the PrintReport route"],
            ["Client View", "Read-only HTML snapshot, branded by partner logo when present"],
            ["Implementation Workbook", "openpyxl workbook generated from (D)"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* I. Versioning & Governance                                      */}
      {/* ------------------------------------------------------------- */}
      <Section letter="I" title="Versioning & Governance">
        <P>
          Every evaluation follows a four-state lifecycle. Finalisation promotes the live
          assessment to an immutable snapshot in <Code>assessment_versions</Code>.
        </P>
        <Table
          head={["Status", "Entry rule", "Effect"]}
          rows={[
            ["In Progress", "Default while inputs &lt; 100 % complete", "Fully editable"],
            ["Ready for Review", "Required fields 100 % complete", "Editable; visible on Consultant dashboard"],
            ["Finalized", "Consultant triggers finalize-version", "Immutable snapshot + version number"],
            ["Shared", "Report emailed to client via email-report", "Terminal state; stamps shared_at on the assessment"],
          ]}
        />
        <Table
          head={["Audit field", "Written on"]}
          rows={[
            ["updated_at", "Every write"],
            ["finalized_at / finalized_version_number", "Finalize action"],
            ["shared_at / last_shared_to", "Report email dispatch"],
            ["RBAC middleware", "Blocks non-admin writes to /api/admin/* except auth and assessments"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* J. Future Extensions                                            */}
      {/* ------------------------------------------------------------- */}
      <Section letter="J" title="Future Extensions">
        <P>
          SmartEvaluate is a module-agnostic <strong>enterprise assessment engine</strong> — not an EWM
          tool. The same pipeline (Inputs → Scoring → Recommendation → ROI → Implementation → Report) is
          reused verbatim to power new PrimeS4 AI platforms; only the domain content changes.
        </P>
        <Table
          head={["PrimeS4 platform", "SmartEvaluate module", "Status"]}
          rows={[
            ["WarehouseX", "SmartEvaluate", "Live"],
            ["TransportX", "SmartEvaluate", "Planned"],
            ["ManufacturingX", "SmartEvaluate", "Future"],
            ["ProcurementX", "SmartEvaluate", "Future"],
            ["FinanceX", "SmartEvaluate", "Future"],
          ]}
        />
        <P>The following components are reused <strong>without modification</strong> across every domain:</P>
        <Table
          head={["Reused engine component", "Role"]}
          rows={[
            ["Question Engine", "Renders and validates the domain question bank"],
            ["Scoring Engine", "Converts answers into business signals and scores"],
            ["Decision Engine", "Maps business signals to recommendation rules and decision confidence"],
            ["Report Engine", "Assembles the branded executive deliverable"],
            ["Versioning", "Lifecycle, immutable snapshots and audit trail"],
            ["Administration Framework", "Admin editing, RBAC and governance"],
          ]}
        />
        <div className="rounded-sm border-l-4 border-[#025979] bg-[#F0F9FF] px-4 py-3 text-[12px] text-[#334155] leading-relaxed">
          Only domain-specific <strong>Question Banks, Decision Rules, ROI Assumptions and Templates</strong>
          change per module — everything else is inherited from the shared engine.
        </div>
        <Table
          head={["Extension point", "Where to extend"]}
          rows={[
            ["New capability", "capability_matrix + recommendation_texts"],
            ["New insight template bank", "SECTION_INSIGHTS map (frontend) keyed by section id"],
            ["New help category", "HELP_CATEGORIES set (backend) + Admin UI dropdown"],
            ["New implementation template", "implementation_templates collection, scenario × strategy × size"],
          ]}
        />
      </Section>

      {/* ------------------------------------------------------------- */}
      {/* K. Engine Reusability                                           */}
      {/* ------------------------------------------------------------- */}
      <Section letter="K" title="Engine Reusability">
        <P>
          SmartEvaluate is a <strong>reusable enterprise assessment engine</strong>, not a one-off
          application. Standing up a new SmartEvaluate module reuses roughly <strong>80–90%</strong> of
          the existing platform — the pipeline, engines, UI shell, versioning and administration are all
          inherited unchanged.
        </P>
        <Table
          head={["Replaced per business domain", "Inherited unchanged (~80–90%)"]}
          rows={[
            ["Question Bank", "Question Engine · Scoring Engine · Decision Engine"],
            ["Decision Rules", "Business Case Engine · Report Engine"],
            ["Capability Library", "PRIVA Insight Engine · Help framework"],
            ["ROI Assumptions", "Versioning · Governance · Audit trail"],
            ["Implementation Templates", "Administration framework · RBAC · UI shell"],
          ]}
        />
        <div className="rounded-sm border-l-4 border-[#025979] bg-[#F0F9FF] px-4 py-3 text-[12px] text-[#334155] leading-relaxed">
          Because only content changes while the machinery stays constant, each new domain (Transport,
          Manufacturing, Procurement, Finance) ships faster and behaves consistently — reinforcing that
          SmartEvaluate is a <strong>platform</strong>, not a single product.
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitive building blocks — kept inline so this page stays self-contained.
// ---------------------------------------------------------------------------

function Section({ letter, title, children }) {
  if (!A_LETTERS.includes(letter)) letter = "•";
  return (
    <section
      data-testid={`logic-section-${letter}`}
      className="mb-5 bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#025979] text-white text-[10px] font-bold">
          {letter}
        </span>
        <h2
          className="text-sm font-semibold text-[#111827] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h2>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function P({ children }) {
  return <p className="text-[12px] text-[#374151] leading-relaxed">{children}</p>;
}

function Code({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded-sm bg-[#F3F4F6] border border-[#E5E7EB] text-[11px] text-[#025979] font-mono">
      {children}
    </code>
  );
}

function Formula({ children }) {
  return (
    <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3 text-[12px] text-[#111827] leading-relaxed font-mono">
      {children}
    </div>
  );
}

function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border border-[#E5E7EB] rounded-sm">
        <thead>
          <tr className="bg-[#F3F4F6]">
            {head.map((h, i) => (
              <th
                key={i}
                className="text-left px-3 py-2 text-[#6B7280] font-semibold uppercase tracking-wider text-[10px] border-b border-[#E5E7EB]"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[#E5E7EB] last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-[#1F2937] align-top leading-relaxed">
                  {typeof cell === "string" ? <Rich text={cell} /> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Render simple inline markdown-ish tokens inside table cells. */
function Rich({ text }) {
  // Render <em>..</em> and <strong>..</strong> literally, plus backticks as code.
  const parts = text.split(/(`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("`") && part.endsWith("`") ? (
          <Code key={i}>{part.slice(1, -1)}</Code>
        ) : (
          <span key={i} dangerouslySetInnerHTML={{ __html: part }} />
        ),
      )}
    </>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 font-medium">
      {children}
    </span>
  );
}

function Arrow() {
  return <span className="text-[#9CA3AF]">→</span>;
}
